/**
 * The one place the portal reads live deploy progress from CloudWatch.
 *
 * Every view that shows a live run (a card, a table row, the log page) asks
 * this poller, and a run is one entry here however many views show it. So one
 * run costs one read, never one for each place it is drawn.
 *
 * THE BUDGET. CloudWatch allows 25 filtered requests a second for the whole
 * account, and everybody at Arcanum shares that. The page reads every live
 * run on screen, up to 30, on a cadence that keeps the average at 2 requests
 * a second or under:
 *
 *    1 to  6 runs: every  5 s →  6 /  5 = 1.2 requests a second at most
 *    7 to 15 runs: every 10 s → 15 / 10 = 1.5 requests a second at most
 *   16 to 30 runs: every 15 s → 30 / 15 = 2.0 requests a second at most
 *
 * That is 8% of the account's 25 a second with one tab open on a full fleet
 * view. A run past the 30 is told it is waiting, and gets a slot when one
 * frees.
 *
 * The arithmetic needs one request for each read, and CloudWatch does not
 * always give that. Measured on live runs: a read that finds new lines is one
 * request, but a read that finds none is two (an empty page that says "there
 * may be more", then the page that says there is not). So a run whose read
 * found nothing waits two intervals for its next one. A run in its long quiet
 * plan phase then costs two requests every two intervals: one an interval,
 * the same as a busy one. The numbers above hold either way.
 *
 * The first read of a run already far into its apply is two or three pages.
 * So requests also start no closer than 275 ms apart: 4 a second is the most
 * this tab ever sends, whatever is catching up.
 *
 * Nothing is read while the browser tab is hidden. A throttling error doubles
 * every wait, up to a minute. A run's reads stop the moment the last view of
 * it lets go, which is the moment the run ends.
 */
import { filterLogPage, isMissingStreamError, isThrottlingError } from '@/services/logsService';
import { PROGRESS_FILTER_PATTERN, createProgress, reduceProgress, type DeployProgress } from './deployProgress';

export const MAX_POLLED_RUNS = 30;
/** How often each run is read, by how many runs are being read. */
const CADENCE: { upTo: number; everyMs: number }[] = [
  { upTo: 6, everyMs: 5_000 },
  { upTo: 15, everyMs: 10_000 },
  { upTo: MAX_POLLED_RUNS, everyMs: 15_000 },
];
const MAX_INTERVAL_MS = 60_000;
/** A big deploy's first read is two or three pages. More than this in one go waits for the run's next turn. */
const MAX_PAGES_PER_READ = 5;
/** A page with this many new lines means the run is catching up: the next page is asked for at once. */
const CATCH_UP_PAGE = 200;
/** The least time between the starts of two requests. 275 ms and not 250: timers fire a few ms early, and five
 * starts 248 ms apart fit inside one second. At 275 no second ever holds more than four. */
const REQUEST_GAP_MS = 275;
/** Reads in flight at once. The gap above sets the rate; this only lets a slow answer not hold the others up. */
const WORKERS = 4;
/** How long progress is kept after the last view lets go, so moving between pages does not read it all again. */
const RETAIN_MS = 120_000;

export interface ProgressTarget {
  /** The run's log stream: one for each deploy task, so a second attempt is a new target. */
  stream: string;
  group: string;
  /** When the run started (epoch ms). The first read starts here. */
  startMs: number;
  mode: 'deploy' | 'plan';
  /** Lower is read first when more than thirty runs want a slot. */
  priority: number;
}

export interface ProgressSnapshot {
  progress: DeployProgress;
  /** At least one read has come back, so "no lines yet" is a fact and not a guess. */
  loaded: boolean;
  /** Over the cap of thirty: not being read until a slot frees. */
  waiting: boolean;
}

interface Entry {
  target: ProgressTarget;
  /** One priority for each view that asked. The lowest wins. */
  subscribers: Map<symbol, number>;
  snapshot: ProgressSnapshot;
  /** The read in flight: CloudWatch's page token, and the start time that token belongs to. */
  queryStart: number;
  nextToken?: string;
  /** The newest line seen, and the ids of the lines that share its millisecond. */
  lastTs: number;
  idsAtLastTs: Set<string>;
  /** When this run is next due a read (epoch ms). 0 for a run not read yet. */
  dueAt: number;
  dropTimer?: ReturnType<typeof setTimeout>;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const tabHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

class DeployProgressPoller {
  private entries = new Map<string, Entry>();
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private backoff = 1;
  private watchingVisibility = false;
  /** When the next request may start. Every request waits for its turn here. */
  private nextRequestAt = 0;

  /** Ask for a run's progress. Returns the function that lets go of it. */
  subscribe(target: ProgressTarget): () => void {
    const token = Symbol(target.stream);
    let entry = this.entries.get(target.stream);
    if (!entry) {
      entry = {
        target,
        subscribers: new Map(),
        snapshot: { progress: createProgress(), loaded: false, waiting: false },
        queryStart: target.startMs,
        lastTs: 0,
        idsAtLastTs: new Set(),
        dueAt: 0,
      };
      this.entries.set(target.stream, entry);
    }
    if (entry.dropTimer) {
      clearTimeout(entry.dropTimer);
      entry.dropTimer = undefined;
    }
    entry.subscribers.set(token, target.priority);
    this.watchVisibility();
    this.assignSlots();
    this.schedule(200);

    return () => {
      const current = this.entries.get(target.stream);
      if (!current) return;
      current.subscribers.delete(token);
      if (current.subscribers.size === 0) {
        current.dropTimer = setTimeout(() => {
          if (current.subscribers.size === 0) this.entries.delete(target.stream);
        }, RETAIN_MS);
      }
      this.assignSlots();
    };
  }

  get(stream: string): ProgressSnapshot | undefined {
    return this.entries.get(stream)?.snapshot;
  }

  /** Called after any snapshot changes. */
  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit() {
    for (const listener of this.listeners) listener();
  }

  private wanted(): Entry[] {
    const priority = (entry: Entry) => Math.min(...entry.subscribers.values());
    return [...this.entries.values()]
      .filter((entry) => entry.subscribers.size > 0)
      .sort((a, b) => priority(a) - priority(b) || a.target.startMs - b.target.startMs);
  }

  /** The first thirty by priority are read. The rest are marked as waiting. */
  private assignSlots(): Entry[] {
    const wanted = this.wanted();
    let changed = false;
    wanted.forEach((entry, index) => {
      const waiting = index >= MAX_POLLED_RUNS;
      if (entry.snapshot.waiting !== waiting) {
        entry.snapshot = { ...entry.snapshot, waiting };
        changed = true;
      }
    });
    if (changed) this.emit();
    return wanted.slice(0, MAX_POLLED_RUNS);
  }

  /** How long one run waits between its reads, for this many runs being read. */
  private interval(activeCount: number): number {
    const step = CADENCE.find((c) => activeCount <= c.upTo) || CADENCE[CADENCE.length - 1];
    return Math.min(MAX_INTERVAL_MS, step.everyMs * this.backoff);
  }

  private schedule(delay: number) {
    // A turn in flight picks up newly due runs itself, and books the next turn when it ends.
    if (this.running) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.tick();
    }, delay);
  }

  private watchVisibility() {
    if (this.watchingVisibility || typeof document === 'undefined') return;
    this.watchingVisibility = true;
    document.addEventListener('visibilitychange', () => {
      // Back on the tab: read at once. A hidden tab's turn ends without booking the next.
      if (document.visibilityState === 'visible') this.schedule(0);
    });
  }

  /** Wait for this request's turn: request starts are at least REQUEST_GAP_MS apart. */
  private async requestTurn() {
    const now = Date.now();
    const at = Math.max(now, this.nextRequestAt);
    this.nextRequestAt = at + REQUEST_GAP_MS;
    if (at > now) await sleep(at - now);
  }

  /** One turn: read every run that is due, a few at a time, then book the next turn for the run due soonest. */
  private async tick() {
    if (this.running || tabHidden()) return;
    this.running = true;
    let throttled = false;
    let every = 0;
    try {
      // Runs can ask for a slot while this turn is in flight, so the list is taken afresh until nothing is due.
      const nextDue = () => {
        const active = this.assignSlots();
        const due = active.filter((entry) => Date.now() >= entry.dueAt);
        // Claim them now, so two workers never take the same run.
        every = this.interval(active.length);
        for (const entry of due) entry.dueAt = Date.now() + every;
        return due;
      };
      let queue = nextDue();
      while (queue.length > 0 && !throttled && !tabHidden()) {
        const batch = queue;
        const worker = async () => {
          while (batch.length > 0 && !throttled && !tabHidden()) {
            const entry = batch.shift()!;
            if (entry.subscribers.size === 0) continue;
            try {
              const foundNew = await this.read(entry);
              // A read that found nothing cost two requests, so this run's next read is two intervals away.
              // (A run still catching up is due at once: `read` says so itself.)
              if (!foundNew && entry.dueAt > 0) entry.dueAt += every;
            } catch (error) {
              if (isThrottlingError(error)) {
                throttled = true;
                // The read did not happen: it is due again as soon as the back-off allows.
                entry.dueAt = 0;
                return;
              }
              // Any other failure: leave the run as it was. It is read again on its next turn.
              console.warn('Could not read deploy progress', entry.target.stream, error);
            }
          }
        };
        await Promise.all(Array.from({ length: WORKERS }, worker));
        queue = throttled ? [] : nextDue();
      }
      this.backoff = throttled ? Math.min(this.backoff * 2, 12) : Math.max(1, this.backoff / 2);
    } finally {
      this.running = false;
      const active = this.assignSlots();
      if (active.length > 0) {
        const soonest = Math.min(...active.map((entry) => entry.dueAt));
        const wait = throttled ? this.interval(active.length) : Math.max(REQUEST_GAP_MS, soonest - Date.now());
        this.schedule(wait);
      }
    }
  }

  /**
   * Read the new progress lines of one run and fold them into the run's progress.
   * Returns whether it found any.
   */
  private async read(entry: Entry): Promise<boolean> {
    const { target } = entry;
    let progress = entry.snapshot.progress;
    let pages = 0;
    let found = 0;
    let catchingUp = false;
    try {
      do {
        await this.requestTurn();
        const page = await filterLogPage({
          group: target.group,
          streams: [target.stream],
          pattern: PROGRESS_FILTER_PATTERN,
          startTime: entry.queryStart,
          nextToken: entry.nextToken,
        });
        pages += 1;
        const fresh = [];
        for (const event of page.events) {
          const ts = event.timestamp ?? 0;
          const id = event.eventId ?? `${ts}:${event.message}`;
          // The next read starts AT the newest line's time, not after it: several lines can share a
          // millisecond (12 did in one real run) and arrive in separate batches. The ids tell them apart.
          if (ts < entry.lastTs || (ts === entry.lastTs && entry.idsAtLastTs.has(id))) continue;
          if (ts > entry.lastTs) {
            entry.lastTs = ts;
            entry.idsAtLastTs = new Set();
          }
          entry.idsAtLastTs.add(id);
          fresh.push(event);
        }
        progress = reduceProgress(progress, fresh, target.mode);
        found += fresh.length;
        entry.nextToken = page.nextToken;
        catchingUp = fresh.length >= CATCH_UP_PAGE;
        // A page with a few new lines is the whole answer for this turn: whatever the token still holds is
        // read from the newest line seen on the next turn. That keeps a busy run at one request a read.
        // An empty page with a token must be followed: the lines may be further on in the range.
        if (entry.nextToken && fresh.length > 0 && !catchingUp) entry.nextToken = undefined;
      } while (entry.nextToken && pages < MAX_PAGES_PER_READ);
    } catch (error) {
      if (isThrottlingError(error)) throw error;
      // No stream yet means the task has not written a line: that is the "Starting" phase, not a fault.
      if (!isMissingStreamError(error)) {
        // A page token can go stale. Drop it and start the next read from the newest line seen.
        entry.nextToken = undefined;
        if (entry.lastTs) entry.queryStart = entry.lastTs;
        throw error;
      }
    }
    // The read is whole (no token left): the next one starts from the newest line seen.
    if (!entry.nextToken && entry.lastTs) entry.queryStart = entry.lastTs;
    // Still catching up (pages left, or the last page was full): carry on at once, not an interval later.
    if (entry.nextToken || catchingUp) entry.dueAt = 0;

    if (progress !== entry.snapshot.progress || !entry.snapshot.loaded) {
      entry.snapshot = { ...entry.snapshot, progress, loaded: true };
      this.emit();
    }
    return found > 0;
  }
}

export const deployProgressPoller = new DeployProgressPoller();
